/**
 * PeerSystemsPanel · Sovereign peers of the Federation.
 *
 * NOT a satellite list. Peers are sovereign systems the Cathedral confers
 * with as equals. Currently: the NextXus Truth Verification System
 * (business & legal node).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export default function PeerSystemsPanel() {
  const [peers, setPeers] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.peers()
      .then((d) => { if (alive) setPeers(d.peers || []); })
      .catch((e) => { if (alive) setErr(e?.message || "offline"); });
    return () => { alive = false; };
  }, []);

  if (err || peers.length === 0) return null;

  return (
    <div
      data-testid="peer-systems-panel"
      className="mb-8 border border-[var(--gold)] bg-[rgba(255,213,79,0.04)] p-6"
      style={{ boxShadow: "0 0 18px rgba(255,213,79,0.06)" }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-glow-gold">
            ◇ Sovereign Peers · Best Friends of the Federation
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mt-1">
            confer-only · not satellites · separate logic
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {peers.map((p) => {
          const pending = p.url_status === "pending";
          return (
            <div
              key={p.id}
              data-testid={`peer-${p.id}`}
              className="border border-[var(--chrome)] bg-[var(--void-2)] p-5"
            >
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="font-display text-lg text-glow-gold leading-tight">
                  {p.name}
                </div>
                {pending && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[var(--magenta)] text-glow-magenta">
                    coming online
                  </span>
                )}
              </div>
              {p.tagline && (
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-glow-cyan mb-2">
                  {p.tagline}
                </div>
              )}
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)] mb-3">
                {p.role}
              </div>
              <p className="text-[var(--text-2)] text-sm leading-relaxed mb-3">
                {p.description}
              </p>
              {p.url_note && (
                <div className="font-mono text-[9px] uppercase tracking-[0.20em] text-glow-magenta mb-3 px-2 py-1 border border-[var(--magenta)] inline-block">
                  ◇ {p.url_note}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {p.url && (
                  pending ? (
                    <Link
                      to="/peers/truth-verifier"
                      data-testid={`peer-link-${p.id}`}
                      className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,213,79,0.08)]"
                    >
                      Learn more ▸
                    </Link>
                  ) : (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`peer-link-${p.id}`}
                      className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,213,79,0.08)]"
                    >
                      Visit ↗
                    </a>
                  )
                )}
                {(p.consults_for || []).slice(0, 2).map((c) => (
                  <span
                    key={c}
                    className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 border border-[var(--chrome)] text-[var(--text-3)]"
                  >
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
